import { Alert, Button, Card, Descriptions, Space, Spin, Tag, Typography } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'

import {
  ordersGetFulfillmentOrderTrackingStatus,
  type FulfillmentOrderRoot,
  type FulfillmentOrderTrackingResponse,
} from '@repo/sdk'
import { formatKstDateTime } from '../../utils/formatKstDateTime'

interface ShippingTrackingCardProps {
  order: FulfillmentOrderRoot
  /** When provided, renders a "change tracking" action next to the tracking number. */
  onChangeTracking?: () => void
}

export function ShippingTrackingCard({ order, onChangeTracking }: ShippingTrackingCardProps) {
  const { t } = useTranslation()

  const isQuickDirect = order.shippingOption === 'QUICK_DIRECT'
  const shippingOptionLabel = isQuickDirect
    ? t('common.shipping.quickDirectDelivery')
    : t('common.shipping.standardShipping')

  const carrierLabel = isQuickDirect
    ? '-'
    : order.carrier
      ? t(`common.carriers.${order.carrier}`)
      : '-'

  const trackingNumber = isQuickDirect ? '-' : (order.trackingNumber ?? '-')
  const hasTracking = !isQuickDirect && Boolean(order.trackingNumber)

  const { data, isFetching } = useQuery<{ data?: FulfillmentOrderTrackingResponse }>({
    queryKey: ['fo-tracking-status', order.fulfillmentOrderId],
    queryFn: () =>
      ordersGetFulfillmentOrderTrackingStatus({
        path: { fulfillmentOrderId: order.fulfillmentOrderId },
      }),
    enabled: hasTracking,
    // 외부 택배사 조회(스마트택배) 호출 비용 때문에 1분 안의 다시 그리기·창 복귀에는 다시 부르지 않는다(개발항목 294).
    staleTime: 60_000,
  })
  const response = data?.data
  const tracking = response?.status === 'OK' ? response.tracking : null

  const levelColor: Record<number, string> = {
    1: 'default',
    2: 'blue',
    3: 'cyan',
    4: 'geekblue',
    5: 'orange',
    6: 'green',
  }

  return (
    <Card title={t('pages.orders.sections.shippingTracking')} bordered>
      <Descriptions column={1} size="small">
        <Descriptions.Item label={t('pages.orders.fields.shippingOption')}>
          {isQuickDirect ? <Tag color="blue">{shippingOptionLabel}</Tag> : shippingOptionLabel}
        </Descriptions.Item>
        <Descriptions.Item label={t('pages.orders.fields.courierCompany')}>
          {carrierLabel}
        </Descriptions.Item>
        <Descriptions.Item label={t('pages.orders.fields.trackingNumber')}>
          <Space size="small">
            <span data-testid="order-detail-tracking-number">{trackingNumber}</span>
            {onChangeTracking && (
              <Button
                size="small"
                icon={<EditOutlined />}
                data-testid="order-detail-change-tracking-btn"
                onClick={onChangeTracking}
              >
                {t('pages.orders.changeTracking.button')}
              </Button>
            )}
          </Space>
        </Descriptions.Item>
        {hasTracking && (
          <Descriptions.Item label={t('pages.orders.fields.shippingStatus')}>
            {isFetching && !response ? (
              <Spin size="small" />
            ) : tracking ? (
              <Tag color={levelColor[tracking.level] ?? 'default'}>
                {t(`pages.orders.liveTracking.level.${tracking.level}`)}
              </Tag>
            ) : response ? (
              <Typography.Text type={response.status === 'NOT_FOUND' ? 'warning' : 'danger'}>
                {t(`pages.orders.liveTracking.status.${response.status}`)}
              </Typography.Text>
            ) : (
              '-'
            )}
          </Descriptions.Item>
        )}
        {tracking?.lastEventDescription && (
          <Descriptions.Item label={t('pages.orders.liveTracking.lastEvent')}>
            {tracking.lastEventDescription}
            {tracking.lastEventLocation ? ` (${tracking.lastEventLocation})` : ''}
          </Descriptions.Item>
        )}
        {tracking?.lastEventAt && (
          <Descriptions.Item label={t('pages.orders.liveTracking.lastEventAt')}>
            {formatKstDateTime(tracking.lastEventAt, 'YYYY-MM-DD HH:mm')}
          </Descriptions.Item>
        )}
        <Descriptions.Item label={t('pages.orders.fields.dispatchDate')}>
          {order.shippedAt ? formatKstDateTime(order.shippedAt, 'YYYY-MM-DD HH:mm') : '-'}
        </Descriptions.Item>
      </Descriptions>
      {response && response.status !== 'OK' && response.status !== 'NO_TRACKING_INFO' && (
        <Alert
          style={{ marginTop: 12 }}
          showIcon
          type={response.status === 'NOT_FOUND' ? 'warning' : 'error'}
          message={t(`pages.orders.liveTracking.status.${response.status}`)}
        />
      )}
    </Card>
  )
}
